import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  DollarSign, CreditCard, TrendingUp, TrendingDown, 
  ArrowUpRight, Calendar, Filter, Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  paid: { bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  initiated: { bg: 'bg-amber-500/20', text: 'text-amber-400' },
  failed: { bg: 'bg-red-500/20', text: 'text-red-400' },
  expired: { bg: 'bg-slate-500/20', text: 'text-slate-400' }
};

// Module-level currency formatter cache. Backend hints `currency: "INR"` in
// /platform/revenue; we default to ₹ since plans are priced in INR. Caching by
// currency means the same function reference is returned across renders, which
// keeps any consumer (chart, table, summary cards) stable.
const _CURRENCY_FORMATTERS = {};
function getCurrencyFormatter(currency) {
  const key = currency === 'USD' ? 'USD' : 'INR';
  if (!_CURRENCY_FORMATTERS[key]) {
    const sym = key === 'USD' ? '$' : '\u20B9';
    const locale = key === 'USD' ? 'en-US' : 'en-IN';
    _CURRENCY_FORMATTERS[key] = (n) => `${sym}${Number(n || 0).toLocaleString(locale)}`;
  }
  return _CURRENCY_FORMATTERS[key];
}

export default function SuperAdminPayments() {
  const [transactions, setTransactions] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, [statusFilter, methodFilter]);

  const fetchData = async () => {
    const token = localStorage.getItem('superAdminToken');
    try {
      const [txnRes, summaryRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/payments/transactions`, {
          params: { 
            ...(statusFilter !== 'all' && { status: statusFilter }),
            ...(methodFilter !== 'all' && { payment_method: methodFilter })
          },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payments/revenue-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/platform/revenue`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTransactions(txnRes.data);
      setRevenueSummary(summaryRes.data);
      setRevenueAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  // Stable currency formatter — same reference for the same currency across
  // every render (see _CURRENCY_FORMATTERS cache above).
  const fmtINR = getCurrencyFormatter(revenueAnalytics?.currency);
  const planLabels = revenueAnalytics?.plan_labels || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Payment Management
        </h1>
        <p className="text-slate-400 mt-1">Track revenue and manage transactions</p>
      </div>

      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="This Month"
          value={fmtINR(revenueSummary?.this_month)}
          icon={DollarSign}
          color="emerald"
          subtitle={`${revenueSummary?.total_transactions_this_month || 0} transactions`}
        />
        <MetricCard
          title="Last Month"
          value={fmtINR(revenueSummary?.last_month)}
          icon={Calendar}
          color="blue"
          subtitle="Previous period"
        />
        <MetricCard
          title="MRR"
          value={fmtINR(revenueSummary?.mrr)}
          icon={TrendingUp}
          color="violet"
          subtitle="Monthly recurring"
        />
        <MetricCard
          title="This Year"
          value={fmtINR(revenueSummary?.this_year)}
          icon={ArrowUpRight}
          color="amber"
          subtitle="Total revenue YTD"
        />
      </div>

      {/* Revenue Chart */}
      {revenueAnalytics?.daily_trend && (
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-violet-400" />
              Revenue Trend (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueAnalytics.daily_trend}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748b"
                    tickFormatter={(val) => new Date(val).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#64748b" tickFormatter={(val) => fmtINR(val)} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(val) => [fmtINR(val), 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue by Plan and Method */}
      {revenueAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Revenue by Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenueAnalytics.revenue_by_plan || {}).map(([plan, amount]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <span className="text-slate-300">{planLabels[plan] || plan.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                    <span className="text-white font-bold">{fmtINR(amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Revenue by Payment Method</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(revenueAnalytics.revenue_by_method || {}).map(([method, amount]) => (
                  <div key={method} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-300 capitalize">{method}</span>
                    </div>
                    <span className="text-white font-bold">{fmtINR(amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transactions Table */}
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white text-lg">Recent Transactions</CardTitle>
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Status</SelectItem>
                <SelectItem value="paid" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Paid</SelectItem>
                <SelectItem value="initiated" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Pending</SelectItem>
                <SelectItem value="failed" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger className="w-32 bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Methods</SelectItem>
                <SelectItem value="stripe" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Stripe</SelectItem>
                <SelectItem value="razorpay" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Razorpay</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No transactions found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Tenant</TableHead>
                  <TableHead className="text-slate-400">Plan</TableHead>
                  <TableHead className="text-slate-400">Amount</TableHead>
                  <TableHead className="text-slate-400">Method</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((txn) => (
                  <TableRow key={txn.id} className="border-slate-700 hover:bg-slate-800/50">
                    <TableCell className="text-white font-medium">{txn.tenant_name}</TableCell>
                    <TableCell>
                      <Badge className="bg-violet-500/20 text-violet-400 capitalize">{txn.plan_id}</Badge>
                    </TableCell>
                    <TableCell className="text-white font-bold">{fmtINR(txn.amount)}</TableCell>
                    <TableCell className="text-slate-300 capitalize">{txn.payment_method}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[txn.status]?.bg} ${STATUS_COLORS[txn.status]?.text}`}>
                        {txn.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {txn.completed_at ? new Date(txn.completed_at).toLocaleString() : new Date(txn.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    emerald: 'bg-emerald-600/20 text-emerald-400',
    blue: 'bg-blue-600/20 text-blue-400',
    violet: 'bg-violet-600/20 text-violet-400',
    amber: 'bg-amber-600/20 text-amber-400'
  };

  return (
    <Card className="bg-slate-900 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-slate-400 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-white mt-2">{value}</p>
            <p className="text-slate-500 text-xs mt-2">{subtitle}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
